import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  applyVendorDirectoryResolutionChangeset,
  getVendorDirectoryResolutionCase,
  previewVendorDirectoryResolutionChangeset,
  recordVendorDirectoryResolutionDecision,
  recomputeVendorDirectoryResolutionCase,
  startVendorDirectoryResolutionReview,
} from "../../api/clientVendorDirectoryResolutionApi";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import "../../styles/casamapaVendorDirectoryAdmin.css";

export default function ClientVendorDirectoryResolutionDetailPage() {
  const { clientViewId, caseId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getVendorDirectoryResolutionCase(clientViewId, caseId);
      setData(body);
    } catch (error) {
      setErrorMessage(error.message || "No se pudo cargar el caso.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [caseId, clientViewId]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (action) => {
    setBusy(true);
    setMessage("");
    setErrorMessage("");
    try {
      await action();
      await load();
    } catch (error) {
      setErrorMessage(error.message || "La acción falló.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <ClientPortalLoading message="Cargando caso de resolución…" />;
  if (errorMessage && !data) return <ClientPortalError message={errorMessage} onRetry={load} />;

  const resolutionCase = data?.case;
  const fieldItems = data?.fieldItems || [];

  return (
    <div className="cvd-page">
      <header className="cvd-page__header">
        <div>
          <Link className="cvd-link" to={`${serviciosBase}/resolucion`}>
            ← Volver a la cola
          </Link>
          <h1>Caso {caseId?.slice(0, 8)}</h1>
          <p className="cvd-page__subtitle">
            Estado: <strong>{resolutionCase?.status}</strong> · Asociación:{" "}
            <strong>{resolutionCase?.associationDisposition}</strong>
          </p>
          {resolutionCase?.staleReason ? (
            <p className="cvd-alert cvd-alert--warning">Caso obsoleto: {resolutionCase.staleReason}</p>
          ) : null}
        </div>
        <div className="cvd-action-row">
          <button
            type="button"
            className="cvd-btn cvd-btn--secondary"
            disabled={busy}
            onClick={() => runAction(() => startVendorDirectoryResolutionReview(clientViewId, caseId))}
          >
            Iniciar revisión
          </button>
          <button
            type="button"
            className="cvd-btn cvd-btn--secondary"
            disabled={busy}
            onClick={() => runAction(() => recomputeVendorDirectoryResolutionCase(clientViewId, caseId))}
          >
            Recomputar
          </button>
          <button
            type="button"
            className="cvd-btn cvd-btn--secondary"
            disabled={busy}
            onClick={() =>
              runAction(async () => {
                const body = await previewVendorDirectoryResolutionChangeset(clientViewId, caseId);
                setPreview(body);
                setMessage("Vista previa del changeset generada.");
              })
            }
          >
            Vista previa
          </button>
          <button
            type="button"
            className="cvd-btn cvd-btn--primary"
            disabled={busy || !data?.applyEnabled}
            onClick={() =>
              runAction(async () => {
                const previewBody =
                  preview || (await previewVendorDirectoryResolutionChangeset(clientViewId, caseId));
                await applyVendorDirectoryResolutionChangeset(clientViewId, caseId, {
                  previewToken: previewBody.previewToken,
                  idempotencyKey: previewBody.changeset?.idempotency_key,
                });
                setMessage("Changeset aplicado.");
              })
            }
          >
            Aplicar cambios
          </button>
        </div>
      </header>

      {message ? <p className="cvd-alert cvd-alert--success">{message}</p> : null}
      {errorMessage ? <p className="cvd-alert cvd-alert--danger">{errorMessage}</p> : null}

      <section className="cvd-panel">
        <h2>Proveedor canónico</h2>
        <p>{data?.vendor?.displayName || "Sin proveedor vinculado"}</p>
        <p>
          Revisión base:{" "}
          {resolutionCase?.canonicalRevision
            ? new Date(resolutionCase.canonicalRevision).toLocaleString()
            : "—"}
        </p>
      </section>

      <section className="cvd-panel">
        <h2>Campos</h2>
        <div className="cvd-table-wrap">
          <table className="cvd-table">
            <thead>
              <tr>
                <th>Campo</th>
                <th>Resultado</th>
                <th>Recomendación</th>
                <th>Confianza</th>
                <th>Materialidad</th>
                <th>Explicación</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {fieldItems.map((item) => (
                <tr key={item.id}>
                  <td><code>{item.fieldKey}</code></td>
                  <td>{item.outcome}</td>
                  <td>{item.recommendedAction}</td>
                  <td>{item.confidenceCategory}</td>
                  <td>{item.materialityCategory}</td>
                  <td>{item.explanationText}</td>
                  <td>
                    <button
                      type="button"
                      className="cvd-btn cvd-btn--secondary"
                      disabled={busy}
                      onClick={() =>
                        runAction(() =>
                          recordVendorDirectoryResolutionDecision(clientViewId, caseId, {
                            fieldItemId: item.id,
                            reasonCode: "ACCEPT_RECOMMENDATION",
                          })
                        )
                      }
                    >
                      Aceptar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {preview?.items?.length ? (
        <section className="cvd-panel">
          <h2>Vista previa del changeset</h2>
          <ul>
            {preview.items.map((item) => (
              <li key={item.fieldKey}>
                <code>{item.fieldKey}</code>: {item.action} · antes={JSON.stringify(item.before)} · después=
                {JSON.stringify(item.after)}
              </li>
            ))}
          </ul>
          {preview.blockers?.length ? (
            <p className="cvd-alert cvd-alert--warning">
              Bloqueadores: {preview.blockers.map((b) => b.message).join("; ")}
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="cvd-panel">
        <h2>Línea de tiempo</h2>
        <ul>
          {(data?.auditEvents || []).map((event) => (
            <li key={event.id}>
              {new Date(event.created_at).toLocaleString()} · {event.event_type}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
